/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid } from '@mui/material';
import {
  SubTitleLayout,
  SubTitleGroup,
  LSmRLgContetntLayout,
} from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import _ from 'lodash';
import CustomGrid from 'components/grids/CustomGrid';
import { MdIvesDtlIspt, MdIvesPerformanceResult } from 'models/md/MdIves';
import { findIvesPfrsltList } from 'apis/md/MdProject';
import { ICellTemplateContext } from '@grapecity/wijmo.grid';
import { CrudCode } from 'models/common/Edit';
import { useCommonModal } from 'hooks/useCommonModal';

type Props = {
  open: boolean;
  close: () => void;
  gbn?: 'ATC' | 'DTL';
  ivesItems: CustomItem[];
  selectedItem?: any;
  callbackFn: (items: CustomItem[]) => void;
};

interface CustomItem extends MdIvesDtlIspt {
  selectYn?: string;
  child?: childProps[];
  selected?: boolean;
}
interface childProps extends MdIvesPerformanceResult {
  mdlDtlIsptId?: string;
}

/**
 * 투자비 database
 */
const MultiUprcInqPopup = ({ open, close, gbn = 'ATC', ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();

  const investmentGridRef = useRef<any>();
  const resultGridRef = useRef<any>();
  const [ivesItems, setIvesItems] = useState<CustomItem[]>(props.ivesItems);
  const [selectedIvesItem, setSelectedIvesItem] = useState<CustomItem>({ uprcDcnTpCd: 'MODEL' });
  const [rowData, setRowData] = useState<CustomItem[]>([]);

  const investmentLayout = [
    {
      binding: 'prdnProcCdNm',
      header: String(t('com.label.공정', '공정')),
      width: '*',
      minWidth: 100,
    },
    {
      binding: 'eqclNm',
      header: String(t('com.label.설비군', '설비군')),
      width: '*',
      minWidth: 100,
    },
    {
      binding: 'dtlIsptNm',
      header:
        gbn === 'ATC'
          ? String(t('com.label.투자항목', '투자항목'))
          : String(t('com.label.비표준항목', '비표준항목')),
      align: 'left',
      width: '*',
      minWidth: 100,
    },
    {
      binding: 'selectYn',
      header: String(t('com.label.선정여부', '선정여부')),
      width: 80,
      cellTemplate: (ctx: ICellTemplateContext) => {
        if (ctx.value === 'Y') return '선정';
        else if (ctx.value === 'H') return '수기 입력';
        else if (ctx.value === 'U') return '<span class="text-blue-500 font-bold">적용</span>';
        else return '';
      },
    },
  ];
  const resultLayout = [
    {
      binding: 'wbsCd',
      header: String(t('com.label.WBS', 'WBS')),
      width: 120,
    },
    {
      binding: 'cnsnAprvDt',
      header: String(t('com.label.품의일자', '품의일자')),
      width: 120,
    },
    {
      binding: 'wbsCdNm',
      header: String(t('com.label.투자명', '투자명')),
      align: 'left',
      width: '*',
      minWidth: 140,
    },
    {
      binding: 'bildPlntCd',
      header: String(t('com.label.플랜트', '플랜트')),
      width: 100,
    },
    {
      binding: 'mdlNm',
      header: String(t('com.label.모델', '모델')),
      width: 100,
    },
    {
      binding: 'specSeq',
      header: String(t('com.label.유사 모델 순위', '유사 모델 순위')),
      width: 120,
    },
    {
      binding: 'ivesDtlDesc',
      header: String(t('com.label.투자 상세', '투자 상세')),
      align: 'left',
      width: '*',
      minWidth: 140,
    },
    {
      binding: 'quotUprc',
      header: String(t('com.label.단가', '단가')),
      width: 90,
    },
  ];

  const initCodes = async () => {
    // console.log('initCodes');
  };

  const btnSearch = (item) => {
    openLoading(true);
    findIvesPfrsltList({
      stndYn: item.stndYn,
      elmPrjId: item.elmPrjId,
      ivesMdlId: item.ivesMdlId,
      uprcDcnTpCd: item.uprcDcnTpCd,
      ivesAtcNm: item.dtlIsptNm,
    })
      .then((res) => {
        const data = (res || []).map((o) => ({
          ...o,
          selected: item.child?.some(
            (x) => x.mdRltDtlId === o.mdRltDtlId && x.crudKey !== CrudCode.DELETE
          ),
        }));
        setRowData(data);
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.데이터 조회 중 오류가 발생했습니다.',
            '데이터 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnApply = () => {
    const ivesItem = investmentGridRef.current.rows
      .filter((row) => row.isSelected)
      .map((row) => row.dataItem)[0];
    const aplyList = resultGridRef.current.rows
      .filter((row) => row.isSelected)
      .map((row) => row.dataItem);

    if (!ivesItem?.mdlDtlIsptId) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.투자항목을 선택해주세요.', '투자항목을 선택해주세요.'),
      });
      return;
    }
    if (aplyList.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택한 데이터가 없습니다.', '선택한 데이터가 없습니다.'),
      });
      return;
    }

    const leftGrid = investmentGridRef.current;

    if (!leftGrid) return;

    // 1. Wijmo의 데이터 관리 객체(CollectionView) 가져오기
    const cv = leftGrid.collectionView;

    // 2. 현재 수정해야 할 좌측 그리드의 원본 객체 찾기
    const targetItem = cv.items.find((item) => item.mdlDtlIsptId === selectedIvesItem.mdlDtlIsptId);

    if (targetItem) {
      // 3. 값 직접 수정 (우측에서 체크된 데이터들의 id 배열을 child에 덮어씀)
      targetItem.child = processMerge(ivesItem.child, aplyList);
      targetItem.selectYn = 'U';
    }
    // 4. grid refresh
    cv.refresh();
    openMessageBar({
      type: 'confirm',
      content: t('com.msg.적용되었습니다.', '적용되었습니다.'),
    });
  };

  const btnSave = () => {
    const updateList = ivesItems.filter((o) => o.selectYn === 'U');
    if (!updateList.length) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.변경된 데이터가 없습니다.', '변경된 데이터가 없습니다.'),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t('com.msg.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        const saveList: any = [];
        updateList.forEach((item) => {
          item.child?.forEach((child, idx) => {
            const tgtMdlNm =
              !child.mdlNm && !child.bildPlntCd
                ? ``
                : `${child.mdlNm || ''} (${child.bildPlntCd || ''})`;
            const obj: CustomItem = _.omit(
              {
                ...item,
                mdlDtlIsptId: child.mdlDtlIsptId,
                mdlIvesAtcId: child.mdlDtlIsptId, // 투자항목 ID 값 매핑
                bildPlntCd: child.bildPlntCd,
                ivesAtcId: child.ivesAtcId,
                ivesAtcNm: child.ivesAtcNm,
                dtlIsptId: child.ivesAtcId,
                dtlIsptNm: child.ivesAtcNm,
                ivesDtlDesc: child.ivesDtlDesc,
                quotUprc: child.quotUprc,
                uprcHdfgInsYn: 'N',
                uprcDcnCtn: child.uprcDcnCtn,
                tgtMdlNm: tgtMdlNm,
                wbsCd: child.wbsCd,
                mdRltDtlId: child.mdRltDtlId,
                stndYn: child.stndYn,
                crudKey: child.crudKey,
              },
              ['child']
            );
            saveList.push(obj);
          });
        });

        props.callbackFn(saveList);
      },
    });
  };

  /**
   * listA: 기존 데이터
   * listB: 새로 합칠 데이터 (ListA에 반영할 후보)
   */
  const processMerge = (listA, listB) => {
    // 1. A의 데이터를 복합 키 기준으로 빠르게 찾기 위해 Map 생성
    const mapA = new Map(listA.map((item) => [`${item.mdRltDtlId}`, item]));

    // 2. 결과를 담을 배열 (ListA 기반으로 시작)
    const result = [...listA];

    // 3. ListB를 순회하며 A에 없으면 추가
    listB.forEach((itemB) => {
      const keyB = `${itemB.mdRltDtlId}`;
      if (!mapA.has(keyB)) {
        result.push({ ...itemB, crudKey: CrudCode.CREATE });
      }
    });

    // 4. ListA에 있지만 ListB에 없으면 삭제 처리
    // ListB를 Map으로 변환하여 존재 여부 확인
    if (listA.length > 0) {
      const mapB = new Map(listB.map((item) => [`${item.mdRltDtlId}`, item]));

      result.forEach((item) => {
        // 이미 'C'가 붙은 항목은 제외하고, A에 있었으나 B에 없는 항목에 'D' 부여
        if (item.crudKey !== CrudCode.CREATE && !mapB.has(`${item.mdRltDtlId}`)) {
          item.crudKey = CrudCode.DELETE;
        }
      });
    }

    // 5. 초기 데이터를 매핑하는 경우 listA에 1번째 객체를 빼고 다음 객체를 'U'로 설정
    // 데이터 중복 등록 방지
    if (listA.length === 1 && !listA[0].mdRltDtlId) {
      const originData = result.shift();
      result[0].crudKey = 'U';
      result[0].mdlDtlIsptId = originData.mdlDtlIsptId;
    }

    return result;
  };

  // 투자항목 grid function
  //  =======================================================
  const onInitialized = (grid) => {
    investmentGridRef.current = grid;

    grid.hostElement.addEventListener('click', function (e) {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      // ht.panel이 GridPanel의 'ColumnHeader' 타입인지 확인
      if (ht.panel === grid.columnHeaders) return;

      const item = grid.rows[ht.row].dataItem;
      if (item) {
        setSelectedIvesItem(item);
      }
    });
  };

  const onLoadedRows = (grid) => {
    //
  };
  //  =======================================================

  // 투자비 Database grid function
  //  =======================================================
  const onInitialized2 = (grid) => {
    resultGridRef.current = grid;
  };

  const onLoadedRows2 = (grid) => {
    setTimeout(() => {
      grid.deferUpdate(() => {
        // grid.rows를 순회하며 원본 데이터(dataItem)의 상태를 UI(isSelected)에 반영
        grid.rows.forEach((row) => {
          // row.dataItem은 우리가 넣어준 rowData 배열의 개별 객체입니다.
          if (row.dataItem && row.dataItem.selected === true) {
            row.isSelected = true; // 이 코드가 Selector의 체크박스를 '체크'되게 만듭니다!
          }
        });
      });
    }, 0);
  };
  //  =======================================================

  useEffect(() => {
    initCodes();
  }, []);
  useEffect(() => {
    btnSearch(selectedIvesItem);
  }, [selectedIvesItem]);

  const dialogButtons = [
    <Button key={'apply'} css={IconButton.button} className="edit" onClick={btnApply} disableRipple>
      {t('com.label.적용', '적용')}
    </Button>,
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={btnSave}
      disableRipple
      disabled={ivesItems.every((o) => o.selectYn !== 'U')}
    >
      {t('com.button.저장', '저장')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={
        gbn === 'ATC'
          ? String(t('md.label.투자항목 Mapping', '투자항목 Mapping'))
          : String(t('md.label.상세 점검사항 Mapping', '상세 점검사항 Mapping'))
      }
      size="xl"
      open={open}
      onClose={close}
      buttons={dialogButtons}
    >
      <LSmRLgContetntLayout>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>
                {gbn === 'ATC'
                  ? t('com.label.투자항목', '투자항목')
                  : t('com.label.상세 점검사항', '상세 점검사항')}
              </h3>
              <span className="total">
                {t('com.label.총', '총')}
                <span>{(ivesItems || []).length.toLocaleString()}</span>
                {t('com.label.건', '건')}
              </span>
            </SubTitleGroup>
          </SubTitleLayout>
          <CustomGrid
            id="investmentGrid"
            layoutDefinition={investmentLayout}
            rowData={ivesItems}
            height={500}
            isReadOnly={true}
            isFilter={false}
            isSelector={true}
            autoCheck={true}
            allowPinning={false}
            rowSelection="single"
            align="center"
            initialized={onInitialized}
            loadedRows={onLoadedRows}
          />
        </div>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.투자비 Database', '투자비 Database')}</h3>
              <span className="total">
                {t('com.label.총', '총')}
                <span>{(rowData || []).length.toLocaleString()}</span>
                {t('com.label.건', '건')}
              </span>
            </SubTitleGroup>
          </SubTitleLayout>
          <CustomGrid
            key={`${selectedIvesItem.mdlDtlIsptId}`}
            id="resultGrid"
            layoutDefinition={resultLayout}
            rowData={rowData}
            height={500}
            isReadOnly={true}
            isFilter={false}
            isSelector={true}
            autoCheck={true}
            allowPinning={false}
            rowSelection={
              selectedIvesItem.uprcDcnTpCd === 'MODEL' && gbn === 'ATC' ? 'single' : 'multiple'
            }
            align="center"
            initialized={onInitialized2}
            loadedRows={onLoadedRows2}
          />
        </div>
      </LSmRLgContetntLayout>
    </CustomDialog>
  );
};

export default MultiUprcInqPopup;
